import React, { useEffect, useState } from "react";
import { FaArrowRightLong } from "react-icons/fa6";
import * as xlsx from "xlsx";
import toast from "react-hot-toast";
import productService from "../Services/productService";
import moment from "moment/moment";
import { useLocation, useNavigate } from "react-router-dom";
import pickingService from "../Services/pickingService";
import supplierService from "../Services/supplierService";
import Dropdown from "../Components/Dropdown";
import { LoadingDots } from "../Components/LoadingDots";

const AddPickingList = () => {
  const [file, setFile] = useState();
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");

  const [purchase, setPurchase] = useState("");
  const [description, setDescription] = useState("");
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const getProducts = async () => {
      try {
        setProducts([]);
        let parts = [];
        list.forEach((single) => {
          let partNumber = single["PartNumber"]?.toString();
          if (partNumber) {
            parts.push(partNumber.replace("-", ""));
          }
        });
        if (parts.length > 0) {
          let response = await productService.searchByPart(parts.toString());
          setProducts(response.products);
        }
      } catch (e) {
        console.error(e);
      }
    };
    getProducts();
  }, [list]);

  // Fetch suppliers
  useEffect(() => {
    supplierService.fetchAll().then((res) => {
      setSuppliers(res.suppliers);
    });
  }, []);

  // Handle file select
  const handleFileSelect = (e) => {
    e.preventDefault();
    if (e.target.files) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        setFile(data);
      };
      reader.readAsArrayBuffer(e.target.files[0]);
    }
  };

  // Read file -> JSON list
  const ProceedFileRead = () => {
    if (file) {
      const workbook = xlsx.read(file, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = xlsx.utils.sheet_to_json(worksheet);
      setList(json);
    } else {
      toast.error("Please upload a file!");
    }
  };

  // ✅ Submit picking list
  const handleSubmit = async () => {
    setLoading(true);
    if (!selectedSupplier) {
      toast.error("Please select a supplier!");
      setLoading(false);
      return;
    }

    const picking = {
      purchase: purchase, // ✅ manually added purchase
      description: description,
      date: date,
      supplier: selectedSupplier,
      supplier_invoice_number: supplierInvoiceNumber,
      reference: referenceNumber,
      products: [],
    };

    const pickProducts = list.map((item) => ({
      part: products.find((v) => v.vendor_code === item.PartNumber)?.id || 0,
      quantityPlaced: item.QuantityPlaced || 0,
      box: item.BoxNumber || 0,
      quantity: item.Quantity || 0,
      price: item.price || 0,
      reference: item.Reference || "",
      maker: item.maker || "",
      partnumber: item.partnumber || "",
      description: item.description || "",
      hscode: item.hscode || "",
      origin: item.origin || "",
    }));

    picking.products = pickProducts;

    try {
      console.log("Sending Picking Payload:", picking);
      await pickingService.create(picking);
      toast.success("Picking list created successfully!");
      navigate("/pickinglist");
    } catch (err) {
      console.error("❌ Failed to create picking:", err);
      toast.error("Failed to create picking list!");
    }
  };

  return (
    <div className="mx-16 my-10">
      <div className="bg-accent rounded-xl py-3 mt-2 px-16 flex flex-col items-start w-[100%]">
        {/* Supplier, Description, Date */}
        <div className="flex flex-row gap-4 mb-4 w-full">
          {/* Supplier Dropdown */}
          <div className="flex flex-col w-1/3">
            <label className="primary-label block mb-1 font-bold">
              Supplier
            </label>
            <Dropdown
              style="!col-span-2 w-full"
              selected={selectedSupplier}
              placeholder="Select Supplier"
              onChange={(value) => setSelectedSupplier(value)}
              options={suppliers.map((supplier) => ({
                label: supplier.company,
                value: supplier.id,
              }))}
            />
          </div>

          {/* Description */}
          <div className="flex flex-col w-2/5">
            <label className="primary-label block mb-1 font-bold">
              Purchase
            </label>
            <input
              type="text"
              value={purchase}
              onChange={(e) => setPurchase(e.target.value)}
              className="w-full py-2 text-sm px-4 bg-gray-100 rounded-lg focus:outline-none border border-gray-100 focus:border-gray-300"
              placeholder="Enter the Purchase ID or Number"
            />
          </div>

          {/* Refereence id */}
          <div className="flex flex-col w-[27%]">
            <label className="primary-label block mb-1 font-bold">
              Reference Id
            </label>
            <input
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              className="w-full py-2 text-sm px-4 bg-gray-100 rounded-lg focus:outline-none border border-gray-100 focus:border-gray-300"
              placeholder="Enter the Reference Id"
            />
          </div>
        </div>

        <div className="flex flex-row gap-4 py-2 w-full">
          <div className="flex flex-col w-[50%]">
            <label className="primary-label block mb-1 font-bold">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) =>
                setDate(moment(e.target.value).format("YYYY-MM-DD"))
              }
              className="w-full py-1 text-md px-4 bg-gray-100 rounded-lg focus:outline-none border border-gray-100 focus:border-gray-300"
            />
          </div>
          <div className="flex flex-col w-[50%]">
            <label className="primary-label block mb-1 font-bold">
              Supplier Invoice Number
            </label>
            <input
              type="text"
              value={supplierInvoiceNumber}
              onChange={(e) => setSupplierInvoiceNumber(e.target.value)}
              className="w-full py-2 text-sm px-4 bg-gray-100 rounded-lg focus:outline-none border border-gray-100 focus:border-gray-300"
              placeholder="Enter the Supplier Invoice Number"
            />
          </div>
          <div className="flex flex-col w-[50%]">
            <label className="primary-label block mb-1 font-bold">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full py-2 text-sm px-4 bg-gray-100 rounded-lg focus:outline-none border border-gray-100 focus:border-gray-300"
              placeholder="Enter the Description"
            />
          </div>
        </div>

        {/* File Upload */}
        <div className="w-full flex flex-row items-center justify-center mb-3 mt-10 gap-3">
          <input
            onChange={(e) => handleFileSelect(e)}
            type="file"
            className="w-72 py-1 text-md px-4 bg-gray-100 rounded-full focus:outline-none border border-gray-100 focus:border-gray-300
            file:mr-4 file:py-[7px] file:px-6 file:rounded-full
             file:border-0 file:text-sm file:font-semibold
             file:bg-white file:text-black
              hover:file:bg-primary hover:file:text-white"
          />
          <button
            onClick={ProceedFileRead}
            className="bg-primary px-2 w-32 py-1 rounded-full text-white font-semibold text-lg"
          >
            Proceed
          </button>
        </div>

        <h1 className="font-bold text-sm mb-2">Upload Excel File</h1>
      </div>

      {/* Table Preview */}
      {list.length > 0 && (
        <>
          <div className={`w-[100%] flex mt-10`}>
            <table className="w-full">
              <thead>
                <tr className="bg-primary uppercase text-xs leading-normal w-full text-white">
                  <th className="text-center py-4 text-[12px] w-[6%] rounded-tl-lg">
                    Sr No.
                  </th>
                  <th className="py-2 text-center text-[12px]">Part No.</th>
                  <th className="py-2 text-center text-[12px]">Quantity</th>
                  <th className="py-2 text-center text-[12px]">Price</th>
                  <th className="py-2 text-center text-[12px]">Box Number</th>
                  <th className="py-2 text-center text-[12px]">Maker</th>
                  <th className="py-2 text-center text-[12px]">Part No.</th>
                  <th className="py-2 text-center text-[12px]">Description</th>
                  <th className="py-2 text-center text-[12px]">HS Code</th>
                  <th className="py-2 text-center text-[12px] rounded-tr-xl">
                    Origin
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((data, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-200 bg-gray-100"
                  >
                    <td className="py-4 text-center text-[12px] text-blue-500 font-bold">
                      {index + 1}
                    </td>
                    <td className="py-3 text-center">{data.PartNumber}</td>
                    <td className="py-3 text-center">{data.Quantity}</td>
                    <td className="py-3 text-center">{data.price || "0"}</td>
                    <td className="py-3 text-center">{data.BoxNumber}</td>
                    <td className="py-3 text-center">{data.maker || "N/A"}</td>
                    <td className="py-3 text-center">
                      {data.partnumber || "N/A"}
                    </td>
                    <td className="py-3 text-center">
                      {data.description || "N/A"}
                    </td>
                    <td className="py-3 text-center">{data.hscode || "N/A"}</td>
                    <td className="py-3 text-center">{data.origin || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Create Picking Button */}

          <div className="flex justify-center mt-6">
            <button onClick={handleSubmit} className="button-primary">
              {loading ? <LoadingDots /> : "Create Picking List"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AddPickingList;
